import { NavLink } from 'react-router-dom'
import { useBulkJobs, useProposalSummary, useReviewSummary } from '../hooks/useKnowledge'

const TABS = [
  { to: '/app/knowledge', label: 'Overview', end: true },
  { to: '/app/knowledge/operations', label: 'Operations' },
  { to: '/app/knowledge/domains', label: 'Domains' },
  { to: '/app/knowledge/concepts', label: 'Concepts' },
  { to: '/app/knowledge/graph', label: 'Graph' },
  { to: '/app/knowledge/agent', label: 'Agent' },
  { to: '/app/knowledge/progress', label: 'Progress' },
  { to: '/app/knowledge/opportunities', label: 'Opportunities' },
  { to: '/app/knowledge/proposals', label: 'Proposals' },
  { to: '/app/knowledge/review', label: 'Review' },
  { to: '/app/knowledge/search', label: 'Search preview' },
  { to: '/app/knowledge/explore', label: 'Explore' },
  { to: '/app/knowledge/changes', label: 'Changes' },
  { to: '/app/knowledge/rules', label: 'Rules' },
]

/** The Knowledge section's tabs. The Review tab carries the open item count. */
export function KnowledgeNav() {
  const { data } = useReviewSummary()
  const { data: proposals } = useProposalSummary()
  const { data: jobs } = useBulkJobs()
  const accepting = (jobs?.data ?? []).filter((j) => j.status === 'queued' || j.status === 'running')
  const counts: Record<string, number> = {
    Review: data ? data.counts.generic + data.counts.suspect_merge + data.counts.duplicate : 0,
    Proposals: (proposals?.pending ?? []).reduce((t, p) => t + p.n, 0),
  }
  return (
    <nav className="flex flex-wrap gap-1 border-b">
      {TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            `-mb-px border-b-2 px-3 py-2 text-sm ${
              isActive ? 'border-slate-900 font-medium text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`
          }
        >
          {t.label}
          {(t.label === 'Proposals' || t.label === 'Agent') && accepting.length > 0 && (
            <span
              className="ml-1.5 inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-500"
              title={`Accepting in the background: ${accepting.map((j) => `${j.action} ${j.accepted}/${j.total}`).join(', ')}`}
            />
          )}
          {(counts[t.label] ?? 0) > 0 && (
            <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-amber-800">
              {counts[t.label].toLocaleString()}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  )
}

/** Page frame for the Knowledge section: title, one line of explanation, the tabs. */
export function KnowledgeFrame({ title, description, children }: { title: string; description: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-6 p-6">
      <div className="space-y-3">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="max-w-4xl text-sm text-slate-500">{description}</p>
        </div>
        <KnowledgeNav />
      </div>
      {children}
    </div>
  )
}
